import { ModelJunctionUsersAndOrgsAndPositions } from '../models/junctionUsersAndOrgsAndPositions'
import { ModelOrganizations } from '../models/organizations'
import { OrganizationsManager } from './OrganizationsManager'

export type OrganizationBasicInfo = {
  id: string
  code: string
  name: string
  category: string | null
}

export type OrganizationHierarchyCache = {
  ancestors: {
    byLevel: {
      [level: number]: OrganizationBasicInfo[]
    }
    orderedIds: string[]
    paths: {
      codes: string[]
      names: string[]
      categories: (string | null)[]
    }
  }
  descendants: {
    byLevel: {
      [level: number]: OrganizationBasicInfo[]
    }
    allIds: string[]
    directChildrenIds: string[]
  }
  self: {
    level: number
    isRoot: boolean
    isLeaf: boolean
    path: string
  }
}

/**
 * ModelDataTypesが存在する場合はそのモデル型を、存在しない場合はデフォルト型を返すユーティリティ型
 */
export type ExtractModelType<
  ModelKey extends string,
  DefaultType,
> = typeof global extends { ModelDataTypes: Record<ModelKey, any> }
  ? (typeof global)['ModelDataTypes'][ModelKey]
  : DefaultType

type OrganizationData = ExtractModelType<'organizations', ModelOrganizations>
/**
 * 組織データをラップし、階層構造に関する操作を提供するクラス
 */
export class OrganizationDataObject {
  public hierarchyCache: OrganizationHierarchyCache | null = null

  constructor(
    public data: OrganizationData,
    public manager: OrganizationsManager,
  ) {}

  /**
   * 基本データへの Accessor
   */
  get id(): string {
    return this.data.id
  }
  get code(): string {
    return this.data.code
  }
  get name(): string {
    return this.data.name
  }
  get category(): string | null {
    return this.data.category
  }
  get parent(): string | null {
    return this.data.parent as string | null
  }
  get sort(): number {
    return this.data.sort
  }
  get junctionUsersAndOrgsAndPositions(): ModelJunctionUsersAndOrgsAndPositions[] {
    return this.data.users || []
  }

  /**
   * 階層キャッシュの取得
   */
  getHierarchyCache(): OrganizationHierarchyCache {
    if (!this.hierarchyCache) {
      this.hierarchyCache = this.calculateHierarchyCache()
    }
    return this.hierarchyCache
  }

  /**
   * 階層キャッシュの計算
   */
  private calculateHierarchyCache(): OrganizationHierarchyCache {
    const ancestors = this.manager.getAncestors(this.id)
    const descendants = this.manager.getDescendants(this.id)

    return {
      ancestors: {
        byLevel: this.groupByLevel(ancestors),
        orderedIds: ancestors.map((a) => a.id),
        paths: {
          codes: ancestors.map((a) => a.code),
          names: ancestors.map((a) => a.name),
          categories: ancestors.map((a) => a.category),
        },
      },
      descendants: {
        byLevel: this.groupByLevel(descendants),
        allIds: descendants.map((d) => d.id),
        directChildrenIds: this.manager.getDirectChildren(this.id).map((c) => c.id),
      },
      self: {
        level: ancestors.length,
        isRoot: !this.parent,
        isLeaf: this.manager.getDirectChildren(this.id).length === 0,
        path: [...ancestors.map((a) => a.code), this.code].join('/'),
      },
    }
  }

  /**
   * 組織を階層レベルでグループ化
   */
  private groupByLevel(orgs: OrganizationDataObject[]): {
    [level: number]: OrganizationBasicInfo[]
  } {
    return orgs.reduce(
      (acc, org) => {
        const level = this.manager.getAncestors(org.id).length
        acc[level] = [
          ...(acc[level] || []),
          {
            id: org.id,
            code: org.code,
            name: org.name,
            category: org.category,
          },
        ]
        return acc
      },
      {} as {
        [level: number]: OrganizationBasicInfo[]
      },
    )
  }

  /**
   * 上位N階層の組織を取得
   */
  getAncestorsN(levels: number): OrganizationDataObject[] {
    const cache = this.getHierarchyCache()
    return Object.entries(cache.ancestors.byLevel)
      .filter(([level]) => Number(level) >= cache.self.level - levels)
      .map(([_, org]) => {
        return org?.[0]?.id ? this.manager.getById(org?.[0]?.id) : null
      })
      .filter((org) => org !== null) as OrganizationDataObject[]
  }

  /**
   * 下位N階層の組織を取得
   */
  getDescendantsN(levels: number): OrganizationDataObject[] {
    const cache = this.getHierarchyCache()
    return Object.entries(cache.descendants.byLevel)
      .filter(([level]) => Number(level) <= cache.self.level + levels)
      .flatMap(([_, orgs]) => orgs.map((org) => this.manager.getById(org.id)))
  }

  /**
   * カスタムパス表示の生成
   */
  getCustomPathDisplay(): string {
    const cache = this.getHierarchyCache()
    return cache.ancestors.paths.names
      .map((name, i) => `${cache.ancestors.paths.categories[i] || ''} ${name}`)
      .join(' > ')
  }

  /**
   * 特定の上位組織を持つかどうかを判定
   */
  hasAncestor(ancestorId: string): boolean {
    const cache = this.getHierarchyCache()
    return cache.ancestors.orderedIds.includes(ancestorId)
  }

  /**
   * 特定の下位組織を持つかどうかを判定
   */
  hasDescendant(descendantId: string): boolean {
    const cache = this.getHierarchyCache()
    return cache.descendants.allIds.includes(descendantId)
  }

  /**
   * 特定の組織との階層差を計算
   */
  getLevelDifference(otherOrgId: string): number {
    const otherOrg = this.manager.getById(otherOrgId)
    return this.getHierarchyCache().self.level - otherOrg.getHierarchyCache().self.level
  }

  /**
   * 特定の組織との最も近い共通の上位組織を取得
   */
  findNearestCommonAncestor(otherOrgId: string): OrganizationDataObject | null {
    const otherOrg = this.manager.getById(otherOrgId)
    const thisAncestors = new Set(this.getHierarchyCache().ancestors.orderedIds)
    const otherAncestors = otherOrg.getHierarchyCache().ancestors.orderedIds

    for (const ancestorId of otherAncestors) {
      if (thisAncestors.has(ancestorId)) {
        return this.manager.getById(ancestorId)
      }
    }

    return null
  }

  /**
   * 同じ親を持つ兄弟組織を取得
   */
  getSiblings(): OrganizationDataObject[] {
    if (!this.parent) return []
    return this.manager.getDirectChildren(this.parent).filter((org) => org.id !== this.id)
  }
}
